/**
 * Pure H OS · Inventory · Stock Adjustment editor + list.
 */
import { useCallback, useEffect, useState } from "react";
import { inventory, InvStockItem, InvMeta, InvAdjustment, InvWarehouse } from "../../services/api";

const VENTURE = "orvan";

export default function AdjustmentEditor() {
  const [meta, setMeta] = useState<InvMeta | null>(null);
  const [warehouses, setWarehouses] = useState<InvWarehouse[]>([]);
  const [stockItems, setStockItems] = useState<InvStockItem[]>([]);
  const [adjustments, setAdjustments] = useState<InvAdjustment[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    warehouseId: "",
    stockItemId: "",
    delta: "",
    reason: "breakage",
    costCenterId: "",
    note: "",
  });

  const reload = useCallback(async () => {
    const [m, whs, adj] = await Promise.all([
      inventory.meta(),
      inventory.listWarehouses(VENTURE),
      inventory.listAdjustments(VENTURE),
    ]);
    setMeta(m);
    setWarehouses(whs);
    setAdjustments(adj);
  }, []);

  const reloadStock = useCallback(async () => {
    if (!form.warehouseId) { setStockItems([]); return; }
    setStockItems(await inventory.listStock(VENTURE, { warehouseId: form.warehouseId }));
  }, [form.warehouseId]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { reloadStock(); }, [reloadStock]);

  async function submit() {
    setBusy(true); setError(null);
    try {
      const res = await inventory.postAdjustment(VENTURE, {
        stockItemId: form.stockItemId,
        delta: Number(form.delta),
        reason: form.reason,
        costCenterId: form.costCenterId || undefined,
        note: form.note || undefined,
      });
      alert(`Adjustment posted (${res.adjustment.docNumber}). New quantity: ${res.newQuantity}.`);
      setForm({ ...form, delta: "", note: "" });
      reload();
      reloadStock();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const selectedStock = stockItems.find((s) => s.id === form.stockItemId);

  return (
    <section data-testid="adjustments-page">
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Form */}
        <div className="lg:col-span-2">
          <h2 className="text-lg font-medium mb-1" style={{ color: "var(--ph-ink-strong)" }}>New adjustment</h2>
          <p className="text-xs text-slate-500 mb-4">
            Posts an <code>adjust</code> movement immediately. Requires a reason. Cannot take quantity negative.
          </p>

          <div className="p-4 space-y-3" style={{ background: "var(--ph-surface-card)", border: "1px solid var(--ph-border-subtle)", borderRadius: "var(--ph-radius-md)" }}>
            <FormRow label="Warehouse">
              <select data-testid="adj-warehouse" className="w-full px-2 py-1.5 text-sm border rounded" value={form.warehouseId} onChange={(e) => setForm((f) => ({ ...f, warehouseId: e.target.value, stockItemId: "" }))}>
                <option value="">Select…</option>
                {warehouses.filter((w) => w.active).map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </FormRow>
            <FormRow label="Stock item">
              <select data-testid="adj-stock-item" className="w-full px-2 py-1.5 text-sm border rounded" value={form.stockItemId} onChange={(e) => setForm((f) => ({ ...f, stockItemId: e.target.value }))} disabled={!form.warehouseId}>
                <option value="">Select…</option>
                {stockItems.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.itemRefId} · Lot {s.lotLabel || "—"} · {s.quantity} {s.unit}
                  </option>
                ))}
              </select>
            </FormRow>
            {selectedStock && (
              <div className="text-[11px] text-slate-500 -mt-2" data-testid="adj-current-qty">
                Current: <strong>{selectedStock.quantity} {selectedStock.unit}</strong> ·
                cost {selectedStock.unitCost.baseCurrency} {selectedStock.unitCost.baseAmount?.toFixed(2)}/unit
              </div>
            )}
            <FormRow label="Delta (signed)">
              <input data-testid="adj-delta" type="number" step="any" className="w-full px-2 py-1.5 text-sm border rounded" value={form.delta} onChange={(e) => setForm((f) => ({ ...f, delta: e.target.value }))} placeholder="e.g. -3" />
              <div className="text-[10px] text-slate-400 mt-1">Negative reduces, positive increases.</div>
            </FormRow>
            <FormRow label="Reason">
              <select data-testid="adj-reason" className="w-full px-2 py-1.5 text-sm border rounded" value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}>
                {(meta?.adjustmentReasons || []).map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </FormRow>
            <FormRow label="Cost center ID (optional)">
              <input data-testid="adj-cc" className="w-full px-2 py-1.5 text-sm border rounded" value={form.costCenterId} onChange={(e) => setForm((f) => ({ ...f, costCenterId: e.target.value }))} />
            </FormRow>
            <FormRow label="Note">
              <textarea data-testid="adj-note" className="w-full px-2 py-1.5 text-sm border rounded" rows={2} value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} />
            </FormRow>
            {error && <div className="text-xs text-red-600" data-testid="adj-error">{error}</div>}
            <button
              data-testid="adj-create-btn"
              onClick={submit}
              disabled={busy || !form.stockItemId || !form.delta || !form.reason}
              className="w-full mt-2 px-3 py-2 text-xs font-medium text-white"
              style={{ background: "var(--ph-brand-primary)", borderRadius: "var(--ph-radius-sm)", opacity: busy ? 0.6 : 1 }}
            >
              {busy ? "Posting…" : "Post adjustment"}
            </button>
          </div>
        </div>

        {/* Recent adjustments */}
        <div className="lg:col-span-3">
          <h3 className="text-sm font-medium mb-3" style={{ color: "var(--ph-ink-strong)" }}>Recent adjustments</h3>
          <div className="overflow-hidden" style={{ background: "var(--ph-surface-card)", border: "1px solid var(--ph-border-subtle)", borderRadius: "var(--ph-radius-md)" }}>
            <table className="w-full text-sm" data-testid="adjustments-table">
              <thead className="text-xs text-slate-500 uppercase tracking-wider" style={{ background: "var(--ph-surface)" }}>
                <tr>
                  <th className="text-left px-3 py-2">Doc #</th>
                  <th className="text-left px-3 py-2">Stock item</th>
                  <th className="text-left px-3 py-2">Delta</th>
                  <th className="text-left px-3 py-2">Reason</th>
                  <th className="text-left px-3 py-2">Posted</th>
                </tr>
              </thead>
              <tbody>
                {adjustments.length === 0 && (
                  <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400 text-xs">No adjustments yet.</td></tr>
                )}
                {adjustments.map((a) => (
                  <tr key={a.id} data-testid={`adj-row-${a.id}`} style={{ borderTop: "1px solid var(--ph-border-subtle)" }}>
                    <td className="px-3 py-2 font-mono text-xs">{a.docNumber}</td>
                    <td className="px-3 py-2 text-xs font-mono">{a.stockItemId}</td>
                    <td className="px-3 py-2 text-xs font-medium" style={{ color: a.delta < 0 ? "#b91c1c" : "#15803d" }}>
                      {a.delta > 0 ? "+" : ""}{a.delta} {a.unit}
                    </td>
                    <td className="px-3 py-2 text-xs">{a.reason}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{new Date(a.postedAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}

function FormRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 mb-1">{label}</div>
      {children}
    </label>
  );
}
